import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { useAuth } from "@/lib/AuthContext";
import { getProfile, getMe, updateProfile, changePassword } from "@/services/auth";

/**
 * Account settings dialog for the signed-in user.
 * - Profile tab: read GET /iam/me + GET /auth/me, edit via PATCH /iam/me.
 * - Security tab: POST /auth/change-password (respects logoutOnPasswordChange).
 * Controlled via `open` / `onOpenChange`.
 */
export default function AccountSettings({ open, onOpenChange }) {
  const { toast } = useToast();
  const { user, logout } = useAuth();

  // ── Profile state ──
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileError, setProfileError] = useState("");
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");

  // ── Security state ──
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [pwSaving, setPwSaving] = useState(false);
  const [pwError, setPwError] = useState("");

  // Load profile + claims whenever the dialog opens.
  useEffect(() => {
    if (!open) return;
    let active = true;

    setProfileError("");
    setProfileLoading(true);
    Promise.allSettled([getProfile(), getMe()])
      .then(([profileRes, meRes]) => {
        if (!active) return;
        const profile = profileRes.status === "fulfilled" ? profileRes.value || {} : {};
        const claims = meRes.status === "fulfilled" ? meRes.value || {} : {};

        setEmail(profile.email || claims.email || user?.email || "");
        setFirstName(profile.firstName || claims.given_name || "");
        setLastName(profile.lastName || claims.family_name || "");

        if (profileRes.status === "rejected" && meRes.status === "rejected") {
          setProfileError("Could not load your profile. Please try again.");
        }
      })
      .finally(() => active && setProfileLoading(false));

    return () => {
      active = false;
    };
  }, [open, user?.email]);

  // Reset the password fields whenever the dialog closes.
  useEffect(() => {
    if (open) return;
    setOldPassword("");
    setNewPassword("");
    setConfirmPassword("");
    setPwError("");
  }, [open]);

  async function handleProfileSave(event) {
    event.preventDefault();
    setProfileError("");
    setProfileSaving(true);
    try {
      await updateProfile({ firstName, lastName });
      toast({ title: "Profile updated", description: "Your changes have been saved." });
    } catch (err) {
      setProfileError(err.message || "Could not save your profile.");
    } finally {
      setProfileSaving(false);
    }
  }

  async function handlePasswordChange(event) {
    event.preventDefault();
    setPwError("");

    if (newPassword.length < 8) {
      setPwError("New password must be at least 8 characters.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setPwError("New passwords do not match.");
      return;
    }
    if (newPassword === oldPassword) {
      setPwError("New password must be different from the current one.");
      return;
    }

    setPwSaving(true);
    try {
      const { sessionInvalidated } = await changePassword({ oldPassword, newPassword });
      if (sessionInvalidated) {
        toast({
          title: "Password changed",
          description: "Please sign in again with your new password.",
        });
        onOpenChange?.(false);
        // Session was revoked server-side — send the user back through login.
        logout();
        return;
      }
      toast({ title: "Password changed", description: "Your password has been updated." });
      setOldPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (err) {
      setPwError(err.message || "Could not change your password.");
    } finally {
      setPwSaving(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Account settings</DialogTitle>
          <DialogDescription>Manage your profile and password.</DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="profile" className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="profile">Profile</TabsTrigger>
            <TabsTrigger value="security">Security</TabsTrigger>
          </TabsList>

          {/* ── Profile ── */}
          <TabsContent value="profile" className="pt-2">
            {profileLoading ? (
              <div className="py-10 flex justify-center">
                <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <form onSubmit={handleProfileSave} className="space-y-4">
                {profileError && (
                  <div className="p-3 rounded-lg bg-red-50 text-red-600 text-sm" role="alert">
                    {profileError}
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="acc-email">Email</Label>
                  <Input id="acc-email" type="email" value={email} disabled readOnly />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label htmlFor="acc-firstName">First name</Label>
                    <Input
                      id="acc-firstName"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      disabled={profileSaving}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="acc-lastName">Last name</Label>
                    <Input
                      id="acc-lastName"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      disabled={profileSaving}
                    />
                  </div>
                </div>

                <div className="flex justify-end">
                  <Button type="submit" disabled={profileSaving}>
                    {profileSaving ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Saving…
                      </>
                    ) : (
                      "Save changes"
                    )}
                  </Button>
                </div>
              </form>
            )}
          </TabsContent>

          {/* ── Security ── */}
          <TabsContent value="security" className="pt-2">
            <form onSubmit={handlePasswordChange} className="space-y-4">
              {pwError && (
                <div className="p-3 rounded-lg bg-red-50 text-red-600 text-sm" role="alert">
                  {pwError}
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="acc-oldPassword">Current password</Label>
                <Input
                  id="acc-oldPassword"
                  type="password"
                  autoComplete="current-password"
                  value={oldPassword}
                  onChange={(e) => setOldPassword(e.target.value)}
                  disabled={pwSaving}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="acc-newPassword">New password</Label>
                <Input
                  id="acc-newPassword"
                  type="password"
                  autoComplete="new-password"
                  minLength={8}
                  placeholder="At least 8 characters"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  disabled={pwSaving}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="acc-confirmPassword">Confirm new password</Label>
                <Input
                  id="acc-confirmPassword"
                  type="password"
                  autoComplete="new-password"
                  minLength={8}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  disabled={pwSaving}
                  required
                />
              </div>

              <div className="flex justify-end">
                <Button type="submit" disabled={pwSaving}>
                  {pwSaving ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Updating…
                    </>
                  ) : (
                    "Change password"
                  )}
                </Button>
              </div>
            </form>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
